import "./index.css";
import "react-toastify/dist/ReactToastify.css";
import { Route, Routes, BrowserRouter } from "react-router-dom";
import { Backdrop, CircularProgress, CssBaseline } from "@mui/material";
import { Helmet } from "react-helmet";
import { useSelector } from "react-redux";
import { ToastContainer } from "react-toastify";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { ThemeProvider } from "@mui/material/styles";
import React, { lazy, Suspense, useEffect, useMemo } from "react";
import { bg, ro, enUS } from "date-fns/locale";
import OrderList from "./pages/order/OrderList";
import EVoucherList from "./pages/evoucher/EVoucherList";
import CustomerData from "./pages/CustomerData";
import theme from "./theme";
import { getUserProfile } from "./service/omvApi";
import { getToken, isValidToken, safe } from "./util";
import { DEFAULT_LANGUAGE } from "./constants/sharedConstants";
import { AuthenticatedPage } from "./pages/route/AuthenticatedPage";
import { NotFoundPage } from "./pages/route/NotFoundPage";

import "@fontsource/roboto/300.css";
import "@fontsource/roboto/400.css";
import "@fontsource/roboto/500.css";
import "@fontsource/roboto/700.css";
import { isApplicationBusy } from "./state/applicationControlSlice";
import { LANGUAGE } from "./constants";

// Lazy load components
const LoginAsRegistered = lazy(() =>
  import("./pages/authentication/LoginAsRegistered"),
);
const LoginWithPhoneNumber = lazy(() =>
  import("./pages/authentication/LoginWithPhoneNumber"),
);
const Authenticate = lazy(() => import("./pages/authentication/Authenticate"));
const ForgotPassword = lazy(() =>
  import("./pages/authentication/ForgotPassword"),
);
const ResetPassword = lazy(() =>
  import("./pages/authentication/ResetPassword"),
);
const OrderAuthenticatedAcceptTerms = lazy(() =>
  import("./pages/OrderAuthenticatedAcceptTerms"),
);
const ActivateAccount = lazy(() =>
  import("./pages/authentication/ActivateAccount"),
);

export default function App() {
  const { texts } = useSelector((state) => state.language);
  const token = getToken();
  const applicationBusy = useSelector(isApplicationBusy);

  useEffect(() => {
    if (!localStorage.getItem("language")) {
      localStorage.setItem("language", DEFAULT_LANGUAGE);
    }
    if (token && isValidToken(token)) {
      safe(async () => {
        await getUserProfile();
      });
    }
  }, [token]);

  const language = localStorage.getItem("language") || DEFAULT_LANGUAGE;
  const locale = useMemo(() => {
    switch (language) {
      case LANGUAGE.RO:
      case LANGUAGE.ro:
        return ro;
      case LANGUAGE.BG:
      case LANGUAGE.bg:
        return bg;
      default:
        return enUS;
    }
  }, [language]);

  return (
    <ThemeProvider theme={theme}>
      <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={locale}>
        <Helmet>
          <title>{texts.common.title}</title>
        </Helmet>
        <CssBaseline />
        <ToastContainer position="top-center" />
        <Suspense
          fallback={
            <Backdrop
              sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
              open={applicationBusy}
            >
              <CircularProgress color="inherit" />
            </Backdrop>
          }
        >
          <BrowserRouter>
            <Routes>
              <Route path="/" element={<AuthenticatedPage />}>
                <Route path="order-list" element={<OrderList />} />
                <Route path="ev-list" element={<EVoucherList />} />
                <Route path="customer-data" element={<CustomerData />} />
              </Route>
              <Route
                path="/order-unauthenticated-accept-terms"
                element={<OrderAuthenticatedAcceptTerms />}
              />
              <Route
                path="/login-as-registered"
                element={<LoginAsRegistered />}
              />
              <Route path="/login" element={<LoginWithPhoneNumber />} />
              <Route path="/authenticate" element={<Authenticate />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route
                path="/reset-password/:accessCode"
                element={<ResetPassword />}
              />
              <Route path="/activate-account" element={<ActivateAccount />} />
              <Route path="*" element={<NotFoundPage />} />
            </Routes>
          </BrowserRouter>
        </Suspense>
      </LocalizationProvider>
    </ThemeProvider>
  );
}
